import { useState } from 'react';

import { Select } from '@/components/common/select';
import { Drawer } from '@/components/feedback/drawer';

import { ROLE_LABEL } from '../constants';
import type { UserFilterFormValues, UserListFilters } from '../types';

import styles from './admin-users-filter-drawer.module.scss';

/** The four internal roles only — Dealer is not an internal user (D1/CONTEXT.md), so it is
 *  excluded from the Role filter options. */
const ROLE_OPTIONS = (
  ['super_admin', 'admin', 'operational_associate', 'field_installer'] as const
).map((value) => ({ value, label: ROLE_LABEL[value] }));

/** All three statuses a row can display, so the filter can reach every row it shows. The labels are
 *  `UserStatusChip`'s, and the deployed API accepts all three as filter values
 *  (`InternalUserStatusFilter`) even though only Active/Inactive are *settable* via Enable/Disable
 *  (`InternalUserStatus`) — three enums, three jobs. */
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending_activation', label: 'Activation Pending' },
];

export type AdminUsersFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: UserListFilters;
  onApply: (filters: UserListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<AdminUsersFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const [draft, setDraft] = useState<UserFilterFormValues>({
    role: value.role ?? '',
    status: value.status ?? '',
  });

  const handleApply = () => {
    // '' → omitted (no filter). toListQuery also strips falsy, but keep the applied contract clean.
    onApply({ role: draft.role || undefined, status: draft.status || undefined });
    onClose();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <div className={styles.fields}>
        <div className={styles.field}>
          <span className={styles.label}>Role</span>
          <Select
            aria-label="Role"
            placeholder="Select"
            allowClear
            style={{ width: '100%' }}
            value={draft.role || undefined}
            options={ROLE_OPTIONS}
            onChange={(next) => setDraft((d) => ({ ...d, role: (next as typeof d.role) ?? '' }))}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Status</span>
          <Select
            aria-label="Status"
            placeholder="Select"
            allowClear
            style={{ width: '100%' }}
            value={draft.status || undefined}
            options={STATUS_OPTIONS}
            onChange={(next) =>
              setDraft((d) => ({ ...d, status: (next as typeof d.status) ?? '' }))
            }
          />
        </div>
      </div>
    </Drawer>
  );
}

export function AdminUsersFilterDrawer({ open, ...rest }: AdminUsersFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody key={String(open)} {...rest} />;
}
